// ASK-1 pilot content — single-shot answers paired with shipped tours.
// Intents: "how do i" / "can this app" — never "tour". Tours win on
// "tour" / "walk me through" / "show me" via resolveFireflyDockQuery.

import type { FireflyAskArtifact } from "./tourTypes";
import { FIREFLY_ASK_PILOT_ARTIFACTS_OPS_MFG } from "./pilotAskArtifactsOpsMfg";

export const FIREFLY_ASK_PILOT_ARTIFACTS: readonly FireflyAskArtifact[] = [
  {
    id: "ask-leads-mode",
    companionTourId: "tour-lead-capture",
    intents: [
      "how does leads mode work",
      "what is leads mode",
      "how do i use leads mode",
      "what can i do in leads mode",
    ],
    bucketRelevance: ["sales", "manager"],
    answer:
      "Leads mode is the capture workspace — voice or typed intake on the active lead card, daily checklist, location quick links, and send to CRM when the lead is ready.",
    navigateTo: { mode: "leads", ctaLabel: "Open Leads" },
    highlightTarget: "mode-nav-leads",
  },
  {
    id: "ask-capture-lead",
    companionTourId: "tour-lead-capture",
    intents: ["how do i capture a lead", "record a new lead", "how do i add a lead"],
    bucketRelevance: ["sales"],
    answer:
      "Open Leads mode, start voice or typed capture on the active lead card, fill required fields, then send to your CRM when ready. Unsent captures stay in Firefly until you send them.",
    navigateTo: { mode: "leads", ctaLabel: "Open Leads" },
    highlightTarget: "capture-lead",
  },
  {
    id: "ask-send-lead",
    companionTourId: "tour-crm-handoff",
    intents: [
      "how do i send a lead",
      "send a lead to ghl",
      "submit a captured lead",
      "how do i send to crm",
    ],
    bucketRelevance: ["sales"],
    answer:
      "After capture, use Send on the lead card or expand Firefly — the Unsent leads section lists everything waiting to go out with a one-click send path.",
    navigateTo: { mode: "leads", ctaLabel: "Open Leads" },
    highlightTarget: "send-lead",
  },
  {
    id: "ask-voice-capture",
    intents: [
      "can this app capture leads by voice",
      "can i talk to capture a lead",
      "how do i use voice capture",
    ],
    bucketRelevance: ["sales"],
    answer:
      "Yes — in Leads mode, use the microphone on the capture card. The app transcribes and extracts fields; you review before sending.",
    navigateTo: { mode: "leads", ctaLabel: "Try voice capture" },
    highlightTarget: "capture-voice",
  },
  {
    id: "ask-crm-handoff",
    intents: [
      "how do i open a lead in crm",
      "what happens after i send a lead",
      "where did my lead go",
      "how do i get to my crm",
    ],
    bucketRelevance: ["sales"],
    answer:
      "After Send succeeds, use Open in CRM on the lead card or the toast link. Unsent rows stay in the dock; sent history shows the past 30 days.",
    navigateTo: { mode: "leads", ctaLabel: "Open Leads" },
    highlightTarget: "lead-crm-link",
  },
  {
    id: "ask-opportunities",
    companionTourId: "tour-opportunities-entry",
    intents: [
      "how do i start a proposal",
      "how do i create an opportunity",
      "how do i start a deal",
      "how do i use opportunities",
    ],
    bucketRelevance: ["sales"],
    answer:
      "Open Opportunities mode and tap New opportunity. Pick a product preset, fill the package, and generate proposal PDFs from the list row.",
    navigateTo: { mode: "opportunities", ctaLabel: "Open Opportunities" },
    highlightTarget: "opp-new-opportunity",
  },
  {
    id: "ask-sales-console",
    companionTourId: "tour-sales-console",
    intents: [
      "how do i use sales console",
      "how do i use the customer console",
      "how do i calculate rent to own",
      "where is the rto calculator",
    ],
    bucketRelevance: ["sales"],
    answer:
      "Sales Console holds the daily checklist, follow-up queue, RTO calculator, and sticky notes. Open it from the sales bucket during live lot conversations.",
    navigateTo: { mode: "salesConsole", ctaLabel: "Open Sales Console" },
    highlightTarget: "sc-rto-calc",
  },
  {
    id: "ask-ads-assistant",
    companionTourId: "tour-ads-assistant",
    intents: [
      "how do i use ads assistant",
      "how do i use the ads assistant",
      "what is ads assistant",
      "help with ads assistant",
      "how do i log a social post",
      "how do i log today's post",
      "where do i log facebook posts",
    ],
    bucketRelevance: ["sales", "manager"],
    requiredRoles: ["sales", "sales_rep", "trainer", "marketing", "admin", "owner"],
    answer:
      "Open Ads Assistant, use the Ingestion tab to paste today's post, then Save post & run analysis. Coaching tones appear in Firefly when guidance is enabled.",
    navigateTo: { mode: "adsAssistant", ctaLabel: "Open Ads Assistant" },
    highlightTarget: "ads-log-post",
  },
  {
    id: "ask-ads-capability",
    intents: [
      "can this app help with social posts",
      "does this app do marketing posts",
      "help me with facebook posts",
    ],
    bucketRelevance: ["sales", "manager"],
    answer:
      "Yes — Ads Assistant tracks social activity and surfaces coaching in Firefly when your org has ads guidance enabled. You need ads-runner access to open the mode.",
    navigateTo: { mode: "adsAssistant", ctaLabel: "Open Ads Assistant" },
    highlightTarget: "ads-log-post",
  },
  {
    id: "ask-training-practice",
    companionTourId: "tour-training-practice",
    intents: [
      "how do i practice a sales call",
      "how do i do roleplay",
      "how do i use training mode",
    ],
    bucketRelevance: ["sales", "manager"],
    answer:
      "Open Training mode, pick Roleplay, choose a scenario, and start roleplay. Trainers can author scenarios from Training Authoring in the manager bucket.",
    navigateTo: { mode: "training", ctaLabel: "Open Training" },
    highlightTarget: "training-roleplay-tab",
  },
  {
    id: "ask-sales-intake",
    companionTourId: "tour-sales-intake",
    intents: [
      "how do i submit an order",
      "how do i submit a customer order",
      "how do i send a deal to manufacturing",
    ],
    bucketRelevance: ["sales"],
    answer:
      "Order Intake is where reps submit customer orders. Fill the form, submit, and track your submissions; ops reviewers see the review queue in the same mode.",
    navigateTo: { mode: "salesIntake", ctaLabel: "Open Order Intake" },
    highlightTarget: "intake-form",
  },
  {
    id: "ask-forms-formats",
    companionTourId: "tour-forms-formats",
    intents: ["how do i edit proposal copy", "how do i edit a proposal template", "where are pdf presets"],
    bucketRelevance: ["manager"],
    requiredRoles: ["trainer", "admin", "owner", "org_admin", "org_owner"],
    answer:
      "Documents Editor holds org documents, proposal PDF presets, and sign templates. Open Documents to browse and author forms, or Proposals to edit text overrides per product type.",
    navigateTo: { mode: "formsAndFormats", ctaLabel: "Open Documents Editor" },
    highlightTarget: "ff-surface",
  },
  {
    id: "ask-fulfillment-inventory",
    companionTourId: "tour-fulfillment-inventory",
    intents: [
      "how do i add a unit to inventory",
      "how do i receive a unit on the lot",
      "where is lot inventory",
    ],
    bucketRelevance: ["fulfillment"],
    answer:
      "Fulfillment Inventory lists every unit on the lot. Use New inventory entry to add a cash purchase or repo piece with an optional intake template.",
    navigateTo: { mode: "fulfillmentInventory", ctaLabel: "Open Inventory" },
    highlightTarget: "ff-inventory-list",
  },
  {
    id: "ask-fulfillment-intake",
    companionTourId: "tour-fulfillment-intake",
    intents: [
      "how do i fill an intake form",
      "how do i start an inspection intake",
      "how do i approve an intake form",
    ],
    bucketRelevance: ["fulfillment"],
    answer:
      "Fulfillment Intake forms queue shows needs-filling and needs-review sections. Lot workers Fill pending rows; admins Review before a unit goes active.",
    navigateTo: { mode: "fulfillmentIntake", ctaLabel: "Open Intake forms" },
    highlightTarget: "ff-intake-queue",
  },
  {
    id: "ask-knowledgebase",
    companionTourId: "tour-knowledgebase",
    intents: [
      "how do i search the knowledge base",
      "how do i find a policy",
      "where is the kb library",
    ],
    bucketRelevance: ["manager"],
    answer:
      "Knowledge Base workspace links Library search, Gap Inbox, and the editor. Open Library, search, and open a result row to read the article.",
    navigateTo: { mode: "knowledgebase", ctaLabel: "Open Knowledge Base" },
    highlightTarget: "kb-search",
  },
  {
    id: "ask-kb-author",
    companionTourId: "tour-kb-author",
    intents: [
      "how do i add a kb article",
      "how do i write a knowledge base article",
      "how do i create a kb article",
    ],
    bucketRelevance: ["manager"],
    requiredRoles: ["trainer", "admin", "owner", "org_admin", "org_owner"],
    answer:
      "From Knowledge Base workspace, tap New article to open the editor. Save stores a draft or change proposal; admins publish live org articles.",
    navigateTo: { mode: "knowledgebase", ctaLabel: "Open Knowledge Base" },
    highlightTarget: "kb-new-article",
  },
  {
    id: "ask-location-tools",
    companionTourId: "tour-location-tools",
    intents: [
      "how do i set location links",
      "how do i configure lot urls",
      "where are location substitutions",
    ],
    bucketRelevance: ["manager"],
    answer:
      "Location Tools holds per-lot URL substitutions. Expand Location links, pick edit mode, adjust link types, and save.",
    navigateTo: { mode: "locationTools", ctaLabel: "Open Location Tools" },
    highlightTarget: "lt-links-tile",
  },
  {
    id: "ask-ai-prompting",
    companionTourId: "tour-ai-prompting",
    intents: [
      "how do i edit ai prompts",
      "how do i suggest a prompt change",
      "where is the prompt review queue",
    ],
    bucketRelevance: ["manager"],
    requiredRoles: ["trainer", "admin", "owner"],
    answer:
      "AI Prompting lists editable prompt cards. Suggest edit on a card to propose changes; admins merge suggestions from the review queue.",
    navigateTo: { mode: "aiPrompting", ctaLabel: "Open AI Prompting" },
    highlightTarget: "ap-surface",
  },
  {
    id: "ask-training-author",
    companionTourId: "tour-training-author",
    intents: [
      "how do i add a roleplay scenario",
      "how do i author training scenarios",
      "how do i publish a roleplay scenario",
    ],
    bucketRelevance: ["manager"],
    requiredRoles: ["trainer", "admin", "owner"],
    answer:
      "Training Authoring catalogs roleplay scenarios. Tap New scenario, edit the script and rubric, then publish when ready for reps.",
    navigateTo: { mode: "trainingAuthor", ctaLabel: "Open Training Authoring" },
    highlightTarget: "ta-new-scenario",
  },
  {
    id: "ask-ads-guidance",
    companionTourId: "tour-ads-guidance",
    intents: [
      "how do i curate ads for reps",
      "how do i edit ads categories",
      "where do i set ads policies",
    ],
    bucketRelevance: ["manager"],
    requiredRoles: ["trainer", "marketing", "ads_guidance_editor", "admin", "owner"],
    answer:
      "Ads Guidance (Manager) holds categories, templates, policies, and post tags that Ads Assistant reads. Expand a library section to edit; reps consume the library when logging posts.",
    navigateTo: { mode: "adsGuidance", ctaLabel: "Open Ads Guidance" },
    highlightTarget: "ads-guidance-library",
  },
  {
    id: "ask-admin-org",
    companionTourId: "tour-admin-org",
    intents: [
      "how do i configure my organization",
      "how do i edit org branding",
      "how do i add a location",
    ],
    bucketRelevance: ["admin"],
    requiredRoles: ["admin", "owner", "org_admin", "org_owner"],
    answer:
      "Admin → Org Administration holds org settings (branding, product types) and Locations. Expand Org Settings to edit profile fields, or Locations to add a lot.",
    navigateTo: { mode: "admin", ctaLabel: "Open Admin" },
    highlightTarget: "admin-org-settings",
  },
  {
    id: "ask-admin-members",
    companionTourId: "tour-admin-members",
    intents: [
      "how do i invite a user",
      "how do i add someone to my org",
      "how do i approve a pending user",
    ],
    bucketRelevance: ["admin"],
    requiredRoles: ["admin", "owner", "org_admin", "org_owner"],
    answer:
      "Admin → App User Admin handles pending sign-ups, role assignment, and email invites. Expand Invite User to send a link with a role pre-assigned.",
    navigateTo: { mode: "admin", ctaLabel: "Open Admin" },
    highlightTarget: "admin-users-card",
  },
  ...FIREFLY_ASK_PILOT_ARTIFACTS_OPS_MFG,
];
